import { Menu, MenuProps } from 'antd'
import { Link, useLocation } from 'react-router-dom'

import { GlobalOutlined, TagsFilled, DollarOutlined } from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSignOutAlt } from '@fortawesome/free-solid-svg-icons'

import { useAppState } from 'state'
import { Links, Paths } from 'components/routes/paths'
import { renderFormatMessage } from 'helpers/intl'
import {
  checkDriverAccess,
  isChargerViewerToTenants,
  isDashboardViewerToTenants,
  isManagerToTenants,
  isTenantManager,
  isTenantViewer,
  isViewerToTenants,
} from 'models/user'

import { pieChart18x18SVG } from 'assets/svg/pieChart'
import { transactionWhiteSVG } from 'assets/svg/transaction'
import { tenantWhiteSVG } from 'assets/svg/tenant'
import { infoSVG } from 'assets/svg/clipboard'
import { userWhiteSVG } from 'assets/svg/user'
import { peakShavingIcon } from 'assets/svg/peakShavingIcon'
import { integrationsMarketplaceIconBlack } from 'assets/svg/integrations-marketplace-icon-black'
import { multiArrow } from 'assets/svg/arrow'
import configProvider from 'config'
import EVChargerSVG from 'assets/svg/ev_charging'
import { theme } from 'theme'

interface NavigationOptionProps {
  collapsed: boolean
  onCollapsed: () => void
}

const NavigationOption: React.FC<NavigationOptionProps> = ({ collapsed, onCollapsed }) => {
  const { currentUser, IsMobile, selectedTenant, language, logout, setLanguage } = useAppState()
  const location = useLocation()
  const isMobile = IsMobile()

  const changeLocal = () => {
    switch (language) {
      case 'en':
        setLanguage('fr')
        break
      case 'fr':
        setLanguage('en')
        break
    }
  }

  const handleLogout: MenuProps['onClick'] = (e) => {
    if (e.key === 'logout') {
      logout()
      window.location.replace(Links.auth0login())
    }
  }
  const canDriverAccess =
    currentUser?.role === 'admin' ||
    isTenantManager(selectedTenant) ||
    (currentUser?.role === 'user' && checkDriverAccess(currentUser?.accesses))

  // admin > manager > viewer > charger_viewer
  const canMangeTenant =
    currentUser?.role === 'admin' ||
    isTenantManager(selectedTenant) ||
    (currentUser?.role === 'user' && isManagerToTenants(currentUser.accesses))

  const canViewTenants =
    canMangeTenant ||
    isTenantViewer(selectedTenant) ||
    (currentUser?.role === 'user' && isViewerToTenants(currentUser.accesses))

  const canViewCharger =
    canViewTenants || (currentUser?.role === 'user' && isChargerViewerToTenants(currentUser.accesses))

  const canViewDashboard =
    canViewTenants || (currentUser?.role === 'user' && isDashboardViewerToTenants(currentUser.accesses))
  
  const taxComplianceText = renderFormatMessage('dashboard.navigation.tax', 'Tax Compliance')
  
  return (
    <>
      <Menu defaultSelectedKeys={[location.pathname]} mode="inline" theme="dark">
        {canDriverAccess && (
          <>
            {canViewDashboard && (
              <Menu.Item key={Paths.dashboard}>
                {pieChart18x18SVG}
                <Link to={Links.dashboard} className="paragraph-01-regular">
                  {renderFormatMessage('dashboard.navigation.dashboard', 'Dashboard')}
                </Link>
              </Menu.Item>
            )}
            {canViewCharger && (
              <Menu.Item key={Paths.chargers}>
                <EVChargerSVG color={theme.colors.white} />
                <Link to={Links.chargers()} className="paragraph-01-regular">
                  {renderFormatMessage('dashboard.navigation.chargers', 'Chargers')}
                </Link>
              </Menu.Item>
            )}
            {canViewTenants && (
              <Menu.Item key={Paths.transactions}>
                {transactionWhiteSVG}
                <Link to={Links.transactions()} className="paragraph-01-regular">
                  {renderFormatMessage('dashboard.navigation.transactions', 'Transactions')}
                </Link>
              </Menu.Item>
            )}
          </>
        )}
        {canMangeTenant && (
          <Menu.Item key={Paths.tenants}>
            {tenantWhiteSVG}
            <Link to={Links.tenants()} className="paragraph-01-regular">
              {renderFormatMessage('dashboard.navigation.tenants', 'Sites')}
            </Link>
          </Menu.Item>
        )}
        {canViewTenants && (
          <Menu.Item key={Paths.reports}>
            {infoSVG}
            <Link to={Links.reports()} className="paragraph-01-regular">
              {renderFormatMessage('dashboard.navigation.reports', 'Reports')}
            </Link>
          </Menu.Item>
        )}
        {canMangeTenant && (
          <Menu.Item key={Paths.users}>
            {userWhiteSVG}
            <Link to={Links.users()} className="paragraph-01-regular">
              {renderFormatMessage('dashboard.navigation.users', 'UsersRedesign')}
            </Link>
          </Menu.Item>
        )}
        {currentUser?.role === 'admin' && (configProvider.config.network === 'swtch' || configProvider.config.network === 'EPB') && (
          <Menu.Item key={Paths.demandManagement}>
            {peakShavingIcon}
            <Link to={Links.demandManagement()} className="paragraph-01-regular">
              {renderFormatMessage('dashboard.peakShaving', 'Peak Shaving')}
            </Link>
          </Menu.Item>
        )}
        {currentUser?.role === 'admin' &&
          configProvider.config.network !== 'powercharge' &&
          configProvider.config.network !== 'demo' && (
            <Menu.Item
              key={Paths.tagging}
              title={
                <Link to={Links.tagging()} className="paragraph-01-regular">
                  {renderFormatMessage('dashboard.tagging', 'Tagging')}
                </Link>
              }
            >
              <TagsFilled style={{ fontSize: 18 }} />
              <Link to={Links.tagging()} className="paragraph-01-regular">
                {renderFormatMessage('dashboard.tagging', 'Tagging')}
              </Link>
            </Menu.Item>
          )}
        {canViewDashboard && configProvider.config.communityIdent === 'SWTCH' && configProvider.config.siteTitle === 'SWTCH' && (
          <Menu.Item key={Paths.integrationsMarketplace}>
            {integrationsMarketplaceIconBlack}
            <Link to={Links.integrationsMarketplace()} className="paragraph-01-regular">
              {renderFormatMessage('dashboard.tenantPage.integrationsMarketplaceTab.heading', 'Integrations Marketplace')}
            </Link>
          </Menu.Item>
        )}
        {currentUser?.role === 'admin' && configProvider.config.communityIdent === 'SWTCH' && configProvider.config.siteTitle === 'SWTCH' && (
          <Menu.Item
            key={Paths.tax}
            title={
              <Link to={Links.tax()} className="paragraph-01-regular">
                {taxComplianceText}
              </Link>
            }
          >
            <DollarOutlined style={{ fontSize: 18 }} />
            <Link to={Links.tax()} className="paragraph-01-regular">
              {taxComplianceText}
            </Link>
          </Menu.Item>
        )}
        {isMobile ? (
          <>
            <Menu.SubMenu
              key="language"
              title="EN | FR"
              icon={<GlobalOutlined />}
              style={{ bottom: '52px', position: 'absolute', left: '4%', width: collapsed ? '5rem' : 'fit-content' }}
              theme="light"
            >
              <Menu.Item
                className="mobile-language"
                disabled={language === 'en' ? true : false}
                key="en"
                onClick={changeLocal}
              >
                EN - ENGLISH
              </Menu.Item>
              <Menu.Item
                className="mobile-language"
                disabled={language === 'fr' ? true : false}
                key="fr"
                onClick={changeLocal}
              >
                FR - FRANÇAIS
              </Menu.Item>
            </Menu.SubMenu>
            <Menu.Item
              key="logout"
              icon={<FontAwesomeIcon icon={faSignOutAlt} />}
              onClick={handleLogout}
              style={{ position: 'absolute', bottom: 0, width: '70%' }}
            >
              {renderFormatMessage('dashboard.header.logout', 'Logout')}
            </Menu.Item>
          </>
        ) : null}
      </Menu>
      <div className={`toggleNavigation ${collapsed ? '' : 'expanded'}`} onClick={onCollapsed}>
        {multiArrow}
      </div>
    </>
  )
}

export default NavigationOption
